import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowViewLink } from '@/components/common/RowActions';
import { formatDisplayDateTime } from '@/utils/date';
import { UNNAMED_INSTITUTE_TITLE } from '../constants/instituteSubmission.constants';
import type { InstituteSubmissionListItem } from '../types/instituteSubmission.types';
import { InstituteSubmissionStatusBadge } from './InstituteSubmissionStatusBadge';
import { InstituteSubmissionTypeBadge } from './InstituteSubmissionTypeBadge';

interface GetInstituteSubmissionColumnsArgs {
  searchText: string | undefined;
}

export function getInstituteSubmissionColumns({
  searchText,
}: GetInstituteSubmissionColumnsArgs): ColumnDef<InstituteSubmissionListItem>[] {
  const highlightSearchText = (text: string | null) =>
    text ? <HighlightText text={text} highlightedText={searchText} /> : undefined;

  return [
    createSerialColumn<InstituteSubmissionListItem>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) =>
        row.original.instituteName ? (
          <WrappingCellText className="font-medium">
            <HighlightText text={row.original.instituteName} highlightedText={searchText} />
          </WrappingCellText>
        ) : (
          <EmptyValue />
        ),
    },
    {
      accessorKey: 'code',
      header: 'Code / EIIN',
      cell: ({ row }) => {
        const { code, eiinNumber } = row.original;
        if (!code && !eiinNumber) return <EmptyValue />;
        return (
          <StackedCellText
            primary={highlightSearchText(code) ?? <EmptyValue />}
            primaryClassName="text-xs tabular-nums"
            secondary={highlightSearchText(eiinNumber)}
          />
        );
      },
    },
    {
      accessorKey: 'instituteTypeName',
      header: 'Institute Type',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'submissionType',
      header: 'Submission Type',
      cell: ({ row }) => (
        <InstituteSubmissionTypeBadge submissionType={row.original.submissionType} />
      ),
    },
    {
      accessorKey: 'thanaName',
      header: 'Thana / District',
      cell: ({ row }) => {
        const { thanaName, districtName } = row.original;
        if (!thanaName && !districtName) return <EmptyValue />;
        return <StackedCellText primary={thanaName ?? <EmptyValue />} secondary={districtName} />;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <InstituteSubmissionStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'userName',
      header: 'Submitted By / Employee ID',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.userName} highlightedText={searchText} />}
          primaryClassName="font-medium"
          secondary={highlightSearchText(row.original.hrEmpId)}
        />
      ),
    },
    {
      accessorKey: 'submittedAt',
      header: 'Submitted At',
      cell: ({ row }) => (
        <span className="whitespace-nowrap">{formatDisplayDateTime(row.original.submittedAt)}</span>
      ),
    },
    {
      accessorKey: 'amName',
      header: 'AM Name',
      cell: ({ row }) =>
        row.original.amName ? (
          <WrappingCellText>{row.original.amName}</WrappingCellText>
        ) : (
          <EmptyValue />
        ),
    },
    createActionsColumn<InstituteSubmissionListItem>((submission) => (
      <RowViewLink
        to="/settings/institute-submissions/$submissionId"
        params={{ submissionId: submission.id }}
        aria-label={`View ${submission.instituteName ?? UNNAMED_INSTITUTE_TITLE}`}
      />
    )),
  ];
}
